/**
 * Asset Form Page
 * ===============
 * Create or edit an IT asset.
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { ArrowLeft, Save, Plus, Loader2 } from 'lucide-react'
import { AxiosError } from 'axios'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import Input from '@/components/Input'
import { useAsset, useCreateAsset, useUpdateAsset } from '@/hooks/useAssets'
import { useAssetLocations, useCreateAssetLocation } from '@/hooks/useAssetLocations'
import { useAssetManufacturers, useCreateAssetManufacturer } from '@/hooks/useAssetManufacturers'
import type { AssetCreate, AssetType, AssetStatus, ConsoleType } from '@/types'
import { ASSET_TYPE_LABELS, ASSET_STATUS_LABELS, CONSOLE_TYPE_LABELS } from '@/types'
import { detectConsoleType } from '@/utils/console'

interface LocationState {
  prefill?: Record<string, unknown>
  source?: string
  returnPath?: string
}

export default function AssetForm() {
  const navigate = useNavigate()
  const location = useLocation()
  const { assetId } = useParams<{ assetId: string }>()
  const isEditing = !!assetId

  // Get prefill data from IP lookup modal (via navigation state)
  const locationState = location.state as LocationState | null
  const prefillData = locationState?.prefill
  const returnPath = locationState?.returnPath

  // Fetch existing asset if editing
  const { data: assetData, isLoading: assetLoading } = useAsset(assetId ?? '')
  const asset = assetData?.data

  // Fetch locations and manufacturers for dropdowns
  const { data: locationsData, refetch: refetchLocations } = useAssetLocations()
  const { data: manufacturersData, refetch: refetchManufacturers } = useAssetManufacturers()

  const createAsset = useCreateAsset()
  const updateAsset = useUpdateAsset()
  const createLocation = useCreateAssetLocation()
  const createManufacturer = useCreateAssetManufacturer()

  // State for creating new location inline
  const [showNewLocation, setShowNewLocation] = useState(false)
  const [newLocationName, setNewLocationName] = useState('')

  // State for creating new manufacturer inline
  const [showNewManufacturer, setShowNewManufacturer] = useState(false)
  const [newManufacturerName, setNewManufacturerName] = useState('')

  // State for form submission error
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState<AssetCreate>({
    name: '',
    asset_type: 'server',
    model: null,
    service_tag: null,
    location_id: null,
    manufacturer_id: null,
    // Specifications
    cpu_model: null,
    cpu_cores: null,
    cpu_threads: null,
    ram_gb: null,
    storage_type: null,
    storage_capacity_gb: null,
    storage_count: null,
    raid_level: null,
    network_ports: null,
    network_speed: null,
    power_watts: null,
    power_supply_count: null,
    power_redundant: null,
    // Network
    ip_address: null,
    mac_address: null,
    management_ip: null,
    vlan_id: null,
    gateway: null,
    // Management
    management_url: null,
    firmware_version: null,
    bios_version: null,
    // Console settings
    console_type: null,
    management_username: null,
    management_password: null,
    // Infrastructure source
    infrastructure_source: null,
    firmware_update_available: null,
    firmware_update_version: null,
    // DSM update (Synology)
    dsm_update_available: null,
    dsm_update_info: null,
    // Lifecycle
    purchase_date: null,
    warranty_expiry: null,
    cost_price: null,
    deployment_date: null,
    notes: null,
  })

  const [status, setStatus] = useState<AssetStatus>('unknown')

  // Load existing asset data when editing
  useEffect(() => {
    if (asset && isEditing) {
      setFormData({
        name: asset.name,
        asset_type: asset.asset_type,
        model: asset.model,
        service_tag: asset.service_tag,
        location_id: asset.location_id,
        manufacturer_id: asset.manufacturer_id,
        cpu_model: asset.cpu_model,
        cpu_cores: asset.cpu_cores,
        cpu_threads: asset.cpu_threads,
        ram_gb: asset.ram_gb,
        storage_type: asset.storage_type,
        storage_capacity_gb: asset.storage_capacity_gb,
        storage_count: asset.storage_count,
        raid_level: asset.raid_level,
        network_ports: asset.network_ports,
        network_speed: asset.network_speed,
        power_watts: asset.power_watts,
        power_supply_count: asset.power_supply_count,
        power_redundant: asset.power_redundant,
        ip_address: asset.ip_address,
        mac_address: asset.mac_address,
        management_ip: asset.management_ip,
        vlan_id: asset.vlan_id,
        gateway: asset.gateway,
        management_url: asset.management_url,
        firmware_version: asset.firmware_version,
        bios_version: asset.bios_version,
        console_type: asset.console_type,
        management_username: asset.management_username,
        // Note: password not loaded from API for security
        purchase_date: asset.purchase_date,
        warranty_expiry: asset.warranty_expiry,
        cost_price: asset.cost_price,
        deployment_date: asset.deployment_date,
        notes: asset.notes,
      })
      setStatus(asset.status)
    }
  }, [asset, isEditing])

  // Apply prefill data from IP lookup (only for new assets)
  useEffect(() => {
    if (prefillData && !isEditing) {
      setFormData((prev) => ({
        ...prev,
        name: (prefillData.name as string) ?? prev.name,
        asset_type: (prefillData.asset_type as AssetType) ?? prev.asset_type,
        model: (prefillData.model as string) ?? prev.model,
        service_tag: (prefillData.service_tag as string) ?? prev.service_tag,
        ip_address: (prefillData.ip_address as string) ?? prev.ip_address,
        mac_address: (prefillData.mac_address as string) ?? prev.mac_address,
        cpu_model: (prefillData.cpu_model as string) ?? prev.cpu_model,
        cpu_cores: (prefillData.cpu_cores as number) ?? prev.cpu_cores,
        cpu_threads: (prefillData.cpu_threads as number) ?? prev.cpu_threads,
        ram_gb: (prefillData.ram_gb as number) ?? prev.ram_gb,
        storage_capacity_gb: (prefillData.storage_capacity_gb as number) ?? prev.storage_capacity_gb,
        storage_count: (prefillData.storage_count as number) ?? prev.storage_count,
        storage_type: (prefillData.storage_type as string) ?? prev.storage_type,
        network_ports: (prefillData.network_ports as number) ?? prev.network_ports,
        network_speed: (prefillData.network_speed as string) ?? prev.network_speed,
        firmware_version: (prefillData.firmware_version as string) ?? prev.firmware_version,
        bios_version: (prefillData.bios_version as string) ?? prev.bios_version,
        gateway: (prefillData.gateway as string) ?? prev.gateway,
        management_url: (prefillData.management_url as string) ?? prev.management_url,
        management_ip: (prefillData.management_ip as string) ?? prev.management_ip,
        vlan_id: (prefillData.vlan_id as number) ?? prev.vlan_id,
        power_supply_count: (prefillData.power_supply_count as number) ?? prev.power_supply_count,
        power_redundant: (prefillData.power_redundant as boolean) ?? prev.power_redundant,
        infrastructure_source: (prefillData.infrastructure_source as string) ?? prev.infrastructure_source,
        firmware_update_available: (prefillData.firmware_update_available as boolean) ?? prev.firmware_update_available,
        firmware_update_version: (prefillData.firmware_update_version as string) ?? prev.firmware_update_version,
        notes: (prefillData.notes as string) ?? prev.notes,
      }))
      // Set status from prefill if available
      if (prefillData.status) {
        setStatus(prefillData.status as AssetStatus)
      }

      // Handle manufacturer_name from SSH lookup - find or note for user
      // The manufacturer will be handled through the dropdown or user can create new
      // TODO: Could auto-match manufacturer by name in the future

      // Clear location state to prevent re-applying on navigation
      window.history.replaceState({}, document.title)
    }
  }, [prefillData, isEditing])

  const locations = locationsData?.data ?? []
  const manufacturers = manufacturersData?.data ?? []

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault()
    setSubmitError(null)

    try {
      if (isEditing && assetId) {
        await updateAsset.mutateAsync({
          id: assetId,
          data: { ...formData, status },
        })
        navigate(`/assets/${assetId}`)
      } else {
        const response = await createAsset.mutateAsync(formData)
        // If a returnPath was provided (e.g., from UniFi import), go there instead
        if (returnPath) {
          navigate(returnPath)
        } else {
          navigate(`/assets/${response.data?.asset_id}`)
        }
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ detail?: string }>
      const message = axiosError.response?.data?.detail || 'Failed to save asset'
      setSubmitError(message)
    }
  }

  const handleCreateLocation = async () => {
    if (!newLocationName.trim()) return
    try {
      const response = await createLocation.mutateAsync({ name: newLocationName })
      await refetchLocations()
      setFormData((prev) => ({ ...prev, location_id: response.data?.id ?? null }))
      setShowNewLocation(false)
      setNewLocationName('')
    } catch (error) {
      console.error('Failed to create location:', error)
    }
  }

  const handleCreateManufacturer = async () => {
    if (!newManufacturerName.trim()) return
    try {
      const response = await createManufacturer.mutateAsync({ name: newManufacturerName })
      await refetchManufacturers()
      setFormData((prev) => ({ ...prev, manufacturer_id: response.data?.id ?? null }))
      setShowNewManufacturer(false)
      setNewManufacturerName('')
    } catch (error) {
      console.error('Failed to create manufacturer:', error)
    }
  }

  if (isEditing && assetLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-primary-600" size={32} />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Edit Asset' : 'New Asset'}
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {isEditing ? `Editing ${asset?.asset_id}` : 'Add a new IT asset'}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button
            icon={<Save size={16} />}
            loading={createAsset.isPending || updateAsset.isPending}
            onClick={handleSubmit}
          >
            {isEditing ? 'Save Changes' : 'Create Asset'}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader title="Basic Information" />
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Asset Name *"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Proxmox-Prod01"
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Asset Type *
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={formData.asset_type}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, asset_type: e.target.value as AssetType }))
                  }
                  required
                >
                  {Object.entries(ASSET_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Model"
                value={formData.model ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, model: e.target.value || null }))
                }
                placeholder="e.g., PowerEdge R640"
              />
              <Input
                label="Service Tag / Serial"
                value={formData.service_tag ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, service_tag: e.target.value || null }))
                }
                placeholder="e.g., 29MBXP2"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Location with inline create */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Location
                </label>
                {showNewLocation ? (
                  <div className="flex gap-2">
                    <Input
                      value={newLocationName}
                      onChange={(e) => setNewLocationName(e.target.value)}
                      placeholder="New location name"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateLocation}
                      loading={createLocation.isPending}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowNewLocation(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      value={formData.location_id ?? ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          location_id: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    >
                      <option value="">Select location...</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewLocation(true)}
                      title="Add new location"
                    >
                      <Plus size={16} />
                    </Button>
                  </div>
                )}
              </div>

              {/* Manufacturer with inline create */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Manufacturer
                </label>
                {showNewManufacturer ? (
                  <div className="flex gap-2">
                    <Input
                      value={newManufacturerName}
                      onChange={(e) => setNewManufacturerName(e.target.value)}
                      placeholder="New manufacturer name"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleCreateManufacturer}
                      loading={createManufacturer.isPending}
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowNewManufacturer(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <select
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      value={formData.manufacturer_id ?? ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          manufacturer_id: e.target.value ? Number(e.target.value) : null,
                        }))
                      }
                    >
                      <option value="">Select manufacturer...</option>
                      {manufacturers.map((mfr) => (
                        <option key={mfr.id} value={mfr.id}>
                          {mfr.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNewManufacturer(true)}
                      title="Add new manufacturer"
                    >
                      <Plus size={16} />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  className="w-full max-w-xs px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as AssetStatus)}
                >
                  {Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Specifications */}
        <Card>
          <CardHeader title="Specifications" />
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="CPU Model"
                value={formData.cpu_model ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, cpu_model: e.target.value || null }))
                }
                placeholder="e.g., Intel Xeon Gold 6132"
              />
              <Input
                label="CPU Cores"
                type="number"
                value={formData.cpu_cores ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    cpu_cores: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                placeholder="e.g., 28"
              />
              <Input
                label="CPU Threads"
                type="number"
                value={formData.cpu_threads ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    cpu_threads: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                placeholder="e.g., 56"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="RAM (GB)"
                type="number"
                value={formData.ram_gb ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    ram_gb: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                placeholder="e.g., 256"
              />
              <Input
                label="Storage Type"
                value={formData.storage_type ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, storage_type: e.target.value || null }))
                }
                placeholder="e.g., SSD, HDD, NVMe"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Storage Capacity (GB)"
                type="number"
                value={formData.storage_capacity_gb ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    storage_capacity_gb: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                placeholder="e.g., 480"
              />
              <Input
                label="Drive Count"
                type="number"
                value={formData.storage_count ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    storage_count: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                placeholder="e.g., 2"
              />
              <Input
                label="RAID Level"
                value={formData.raid_level ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, raid_level: e.target.value || null }))
                }
                placeholder="e.g., RAID1, RAID5"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Network Ports"
                type="number"
                value={formData.network_ports ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    network_ports: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                placeholder="e.g., 4"
              />
              <Input
                label="Network Speed"
                value={formData.network_speed ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, network_speed: e.target.value || null }))
                }
                placeholder="e.g., 10GbE, 1GbE"
              />
              <Input
                label="Power (Watts)"
                type="number"
                value={formData.power_watts ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    power_watts: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                placeholder="e.g., 750"
              />
              <Input
                label="PSU Count"
                type="number"
                value={formData.power_supply_count ?? ''}
                onChange={(e) => {
                  const count = e.target.value ? Number(e.target.value) : null
                  setFormData((prev) => ({
                    ...prev,
                    power_supply_count: count,
                    // Auto-disable redundant if PSU count is 1 or less
                    power_redundant: count && count <= 1 ? false : prev.power_redundant,
                  }))
                }}
                placeholder="e.g., 2"
              />
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.power_redundant ?? false}
                  disabled={(formData.power_supply_count ?? 0) <= 1}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, power_redundant: e.target.checked }))
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                />
                <span className={`text-sm ${(formData.power_supply_count ?? 0) <= 1 ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                  Redundant Power Supply
                </span>
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Network Information */}
        <Card>
          <CardHeader title="Network Information" />
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="IP Address"
                value={formData.ip_address ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, ip_address: e.target.value || null }))
                }
                placeholder="e.g., 192.168.10.252"
              />
              <Input
                label="MAC Address"
                value={formData.mac_address ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, mac_address: e.target.value || null }))
                }
                placeholder="e.g., 00:1A:2B:3C:4D:5E"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Management IP (iDRAC/IPMI)"
                value={formData.management_ip ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, management_ip: e.target.value || null }))
                }
                placeholder="e.g., 192.168.1.4"
              />
              <Input
                label="VLAN ID"
                type="number"
                value={formData.vlan_id ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    vlan_id: e.target.value ? Number(e.target.value) : null,
                  }))
                }
                placeholder="e.g., 10"
              />
              <Input
                label="Gateway"
                value={formData.gateway ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, gateway: e.target.value || null }))
                }
                placeholder="e.g., 192.168.10.1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Management */}
        <Card>
          <CardHeader title="Management" />
          <CardContent className="space-y-4">
            <Input
              label="Management URL"
              value={formData.management_url ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, management_url: e.target.value || null }))
              }
              placeholder="e.g., https://192.168.1.4"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Firmware Version"
                value={formData.firmware_version ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, firmware_version: e.target.value || null }))
                }
                placeholder="e.g., 2.80.80.80"
              />
              <Input
                label="BIOS Version"
                value={formData.bios_version ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, bios_version: e.target.value || null }))
                }
                placeholder="e.g., 2.12.2"
              />
            </div>

            {/* Console Settings - only show for servers */}
            {formData.asset_type === 'server' && (
              <>
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Virtual Console Settings
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    Configure remote console access (iDRAC, IPMI) for quick launch from asset view.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Console Type
                    </label>
                    <select
                      value={formData.console_type ?? ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          console_type: (e.target.value || null) as ConsoleType | null,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Not configured</option>
                      {Object.entries(CONSOLE_TYPE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Console Username"
                    value={formData.management_username ?? ''}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, management_username: e.target.value || null }))
                    }
                    placeholder="e.g., root"
                  />
                  <Input
                    label="Console Password"
                    type="password"
                    value={formData.management_password ?? ''}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, management_password: e.target.value || null }))
                    }
                    placeholder={isEditing ? '(unchanged)' : 'Enter password'}
                  />
                </div>
                {/* Auto-detect button */}
                {formData.management_ip && !formData.console_type && (
                  <button
                    type="button"
                    onClick={() => {
                      const detected = detectConsoleType(
                        manufacturers.find(m => m.id === formData.manufacturer_id)?.name,
                        formData.model
                      )
                      if (detected) {
                        setFormData((prev) => ({ ...prev, console_type: detected }))
                      }
                    }}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Auto-detect console type from manufacturer
                  </button>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Lifecycle */}
        <Card>
          <CardHeader title="Lifecycle" />
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Purchase Date"
                type="date"
                value={formData.purchase_date ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, purchase_date: e.target.value || null }))
                }
              />
              <Input
                label="Warranty Expiry"
                type="date"
                value={formData.warranty_expiry ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, warranty_expiry: e.target.value || null }))
                }
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Deployment Date"
                type="date"
                value={formData.deployment_date ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, deployment_date: e.target.value || null }))
                }
              />
              <Input
                label="Cost Price"
                value={formData.cost_price ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, cost_price: e.target.value || null }))
                }
                placeholder="e.g., 4500.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                rows={3}
                value={formData.notes ?? ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value || null }))
                }
                placeholder="Additional notes about this asset..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit error */}
        {submitError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
            {submitError}
          </div>
        )}
      </form>
    </div>
  )
}
